import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Inject,
  InjectionToken,
  Optional,
  OnInit,
} from '@angular/core';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import {
  ButtonConfig,
  ButtonIcons,
  ModalActionType,
  AssetsCategory,
  IconComponent,
  ButtonComponent,
} from '@invessio/ui';

export interface ErrorScreenNavigation {
  goBack(): void;
}

export const ERROR_SCREEN_NAVIGATION = new InjectionToken<ErrorScreenNavigation>(
  'ERROR_SCREEN_NAVIGATION'
);

@Component({
  selector: 'app-error-screen',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, ButtonComponent],
  templateUrl: './error-screen.component.html',
  styleUrls: ['./error-screen.component.css'],
})
export class ErrorScreenComponent implements OnInit {
  readonly AssetsCategory = AssetsCategory;
  readonly ModalActionType = ModalActionType;

  @Input() heading = 'Well, this is embarrassing';
  @Input() subtext =
    "Something broke on our end and we're not entirely sure what happened. We're probably already panicking about it — try again in a bit.";
  @Input() buttonLabel = 'Go Back';
  @Input() supportLabel = 'Contact Support';
  @Input() illustrationSvg = '500-error';
  @Input() theme = '';

  get backButtonConfigs(): ButtonConfig[] {
    return [
      {
        label: this.buttonLabel,
        icon: ButtonIcons.arrowUndo,
        iconPosition: 'left',
        action: ModalActionType.BACK,
        className:
          'flex items-center gap-2 w-full sm:w-auto px-6 py-3 rounded-md text-sm font-semibold',
        customStateClass:
          'bg-ignyte-theme text-sm text-white rounded-[0.5rem] border border-ignyte-button shadow-[0_0.0625rem_0.125rem_rgba(0,0,0,0.05)] cursor-pointer transition-colors duration-150 hover:bg-red-700 active:bg-red-800 flex items-center justify-center gap-2 !py-3',
      },
    ];
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    @Optional()
    @Inject(ERROR_SCREEN_NAVIGATION)
    private navigation?: ErrorScreenNavigation
  ) {}

  ngOnInit(): void {
    const statusParam = this.route.snapshot.queryParamMap.get('status');
    const statusCode = Number(statusParam);

    if (Number.isFinite(statusCode) && statusCode > 0) {
      this.illustrationSvg = `${statusCode}-error`;
    }

    if (statusCode === 403) {
      this.heading = 'Access Denied';
      this.subtext =
        'You do not have the required permissions to view this page. Please contact your system administrator if you believe this is an error.';
    } else if (statusCode === 404) {
      this.heading = 'Page not found';
      this.subtext =
        "The page you're looking for doesn't exist or may have moved. Check the address or head back to the dashboard.";
    }
  }

  goBack(): void {
    if (this.navigation) {
      this.navigation.goBack();
      return;
    }

    this.router.navigate(['/']);
  }

  contactSupport(): void {
    window.location.href = 'mailto:ignytesupport@yrefy.com';
  }
}
